import {Component, computed, inject, input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ProjectBaseStepComponent} from '../../../../../project-base-step/project-base-step.component';
import {Panel} from 'primeng/panel';
import {SharedModule} from 'primeng/api';
import {ProgressBar} from 'primeng/progressbar';
import {SystemRecordingFunctionRowComponent} from '../system-recording-function-row/system-recording-function-row.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {ToastService} from '@frontend/toast';
import {SystemRecordingStateService} from '../../system-recording-state.service';
import {FunctionEntryService} from '../../services/function-entry.service';
import {FunctionEntry, ModuleData} from '../../system-recording.types';
import {LoadingComponent} from '../../../../../../../../shared/components/loading/loading.component';

@Component({
  selector: 'app-system-recording-module-hr',
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    SharedModule,
    ProgressBar,
    SystemRecordingFunctionRowComponent,
    ProjectStepSaveButtonComponent,
    LoadingComponent,
  ],
  templateUrl: './system-recording-module-hr.component.html',
})
export class SystemRecordingModuleHrComponent extends ProjectBaseStepComponent {
  private readonly systemRecordingStateService = inject(SystemRecordingStateService);
  private readonly functionEntryService = inject(FunctionEntryService);
  private readonly toastService = inject(ToastService);

  moduleData = input.required<ModuleData>();
  isReadOnly = input(false);

  private readonly changeDetectionTrigger = signal(0);

  protected isSaving = signal(false);
  protected collapsed = signal(true);

  protected readonly hasChanges = computed(() => {
    this.changeDetectionTrigger();
    return this.systemRecordingStateService.hasModuleChanges(this.moduleData().id);
  });

  protected readonly completedCount = computed(
    () => this.moduleData().functions.filter((f) => this.isFunctionCompleted(f)).length,
  );

  protected readonly areAllFunctionsValid = computed(() =>
    this.moduleData().functions.every((f) => this.isFunctionValid(f)),
  );

  protected readonly totalCount = computed(() => this.moduleData().functions.length);

  protected readonly duplicatesMap = computed(() => {
    const map = new Map<string, boolean>();
    const countMap = new Map<number, number>();

    this.moduleData().functions.forEach((func) => {
      const count = countMap.get(func.directusId) || 0;
      countMap.set(func.directusId, count + 1);
    });

    this.moduleData().functions.forEach((func) => {
      const count = countMap.get(func.directusId) || 0;
      map.set(func.uniqueId, count > 1);
    });

    return map;
  });

  hasDuplicates(funcEntry: FunctionEntry): boolean {
    return this.duplicatesMap().get(funcEntry.uniqueId) || false;
  }

  onFunctionChanged(): void {
    this.changeDetectionTrigger.update((v) => v + 1);
  }

  async saveModule(): Promise<void> {
    this.isSaving.set(true);
    try {
      await this.systemRecordingStateService.saveModule(this.projectId, this.moduleData().id);
      await this.stepState.loadStepStatuses(this.projectId, true);

      await this.toastService.showSuccess(
        'common.success',
        'project.steps.systemRecording.moduleSaved',
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  private isFunctionValid(funcEntry: FunctionEntry): boolean {
    return !funcEntry.plannedAction || this.functionEntryService.isFunctionComplete(funcEntry);
  }

  private isFunctionCompleted(funcEntry: FunctionEntry): boolean {
    return this.functionEntryService.isFunctionComplete(funcEntry);
  }
}
